import { motion } from "framer-motion";
import { MapPin, Sparkles } from "lucide-react";
import { LeafMotif } from "./Reveal";
import HeroLeadForm from "./HeroLeadForm";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const fade = (delay) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] },
});

export default function Hero({ onRequest, onDiscover, onLeadStarted }) {
  return (
    <section data-testid="hero-section" className="relative overflow-hidden bg-[#FAF7F2] pt-24 lg:pt-32 pb-16 lg:pb-24">
      <div className="pointer-events-none absolute -top-24 -right-24 w-[480px] h-[480px] rounded-full bg-[#EAF0EC] blur-3xl opacity-70" />
      <div className="pointer-events-none absolute bottom-0 -left-32 w-[380px] h-[380px] rounded-full bg-[#F7EFE9] blur-3xl opacity-80" />
      <LeafMotif className="pointer-events-none absolute top-28 left-[4%] w-16 h-16 text-[#C5A059]/25 rotate-12 hidden lg:block" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 lg:gap-10 items-center">
        <div className="lg:col-span-7 order-2 lg:order-1 hidden lg:block">
          <motion.div {...fade(0.1)} className="inline-flex items-center gap-2 bg-[#EAF0EC] border border-[#5B7566]/20 text-[#1B3B2B] px-4 py-1.5 rounded-full text-[11px] font-semibold tracking-[0.22em] uppercase">
            <MapPin className="w-3.5 h-3.5 text-[#B85D43]" />
            USA Consultations • Limited Appointments
          </motion.div>

          <h1 className="mt-6 font-serif tracking-tight text-[#1B3B2B] text-[2.5rem] leading-[1.06] sm:text-6xl lg:text-[4.2rem]">
            <MaskedLine delay={0.22}>Understand Your Child</MaskedLine>
            <MaskedLine delay={0.36}>
              <em className="not-italic font-serif italic text-transparent bg-clip-text bg-gradient-to-r from-[#C5A059] via-[#D4AF37] to-[#9E7B3B]">Beyond the Diagnosis</em>
            </MaskedLine>
          </h1>

          <motion.p {...fade(0.55)} className="mt-6 text-base sm:text-lg text-[#5C6E62] max-w-xl leading-relaxed">
            Personalized Ayurvedic assessment integrating Nadi Pariksha, Ayurvedic Brain Types and functional
            developmental patterns for children with speech, attention, sensory, behavioral and developmental concerns.
          </motion.p>

          <motion.div {...fade(0.7)} className="mt-8 flex flex-col sm:flex-row gap-3 sm:items-center">
            <button
              data-testid="hero-discover-braintype-btn"
              onClick={onDiscover}
              className="bg-[#1B3B2B] text-[#FAF7F2] font-semibold tracking-wide text-sm px-8 py-4 rounded-full hover:bg-[#132C20] transition-all duration-300 shadow-[0_12px_32px_rgb(27,59,43,0.25)] hover:shadow-[0_16px_40px_rgb(27,59,43,0.32)] hover:-translate-y-0.5"
            >
              Discover Your Child's Brain Type
            </button>
            <button
              data-testid="hero-request-consultation-btn"
              onClick={() => onRequest()}
              className="border border-[#1B3B2B]/30 text-[#1B3B2B] font-semibold tracking-wide text-sm px-8 py-4 rounded-full hover:border-[#C5A059] hover:bg-[#F4EFE6] transition-all duration-300"
            >
              Request a USA Consultation
            </button>
          </motion.div>

          <motion.p {...fade(0.85)} className="mt-6 text-xs sm:text-sm text-[#5C6E62] tracking-wide">
            15+ Years of Clinical Experience&nbsp;&nbsp;|&nbsp;&nbsp;Pediatric Ayurvedic Care&nbsp;&nbsp;|&nbsp;&nbsp;Limited USA Appointments
          </motion.p>
          <motion.p {...fade(0.95)} className="mt-2 flex items-center gap-2 text-xs text-[#B85D43] font-medium">
            <Sparkles className="w-3.5 h-3.5" />
            Limited in-person appointments available during the USA visit.
          </motion.p>
        </div>

        <div className="lg:col-span-5 order-1 lg:order-2">
          <HeroLeadForm onStarted={onLeadStarted} />
        </div>
      </div>
    </section>
  );
}
